import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModals } from '../../../../../contexts/ModalsContext';
import { ModalActionsEnum } from '../../../../document-table/DocumentTableConstants';
import { useUserDealRestrictions } from '../../../../../auth/user-restrictions';
import { useTeamInfo } from '../../../../../team-id-context';
import { useDealId } from '../../../../../deal-id-context';
import DocumentAttachmentsSidebar from './documentAttachmentsSidebar';
import DealDocumentPickerModal from './DealDocumentPickerModal';
import { FileUploadModal } from '../../../../shared/modals';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queriesV2 } from '../../../../../api';
import { useDocumentState } from '../../../../../contexts/DocumentContext';

interface DocumentAttachmentsSidebarContainerProps {
  activeUuid: string | null;
}

const DocumentAttachmentsSidebarContainer = ({ activeUuid: _activeUuid }: DocumentAttachmentsSidebarContainerProps) => {
  const { activeModal, setActiveModal } = useModals();

  const { teamId } = useTeamInfo();

  const dealId = useDealId();

  const { state: { id: documentId, teamId: documentCreatorTeamId } } = useDocumentState();

  const { canUploadDocument } = useUserDealRestrictions();

  const isUserInCreatorTeam = documentCreatorTeamId === teamId;

  const canUploadExtraAttachments = canUploadDocument && isUserInCreatorTeam;

  const [showDealDocPicker, setShowDealDocPicker] = useState(false);

  const handleFileUploadModal = useCallback(() => {
    if (canUploadDocument) {
      return setActiveModal(ModalActionsEnum.UPLOAD_ATTACHMENT_FILES);
    } else {
      return setActiveModal(null);
    }
  }, [setActiveModal, canUploadDocument]);

  const handleDealDocumentPickerModal = useCallback(() => {
    setShowDealDocPicker(true);
  }, []);

  const refetchAttachmentsConfig = {
    refetchQueries: [
      {
        query: queriesV2.getDocumentExtraAttachments,
        variables: { documentId: String(documentId) },
      },
    ],
  };

  const { data: documentExtraAttachmentsData } = useQuery(queriesV2.getDocumentExtraAttachments, {
    variables: { documentId: String(documentId) },
    skip: !documentId,
    fetchPolicy: 'network-only',
  });

  const [removeDocumentExtraAttachment] = useMutation(mutations.removeDocumentExtraAttachment, refetchAttachmentsConfig);

  const [reorderDocumentExtraAttachments] = useMutation(mutations.reorderDocumentExtraAttachments, refetchAttachmentsConfig);

  const [linkDealDocumentAttachment, { loading: linkLoading }] = useMutation(mutations.linkDealDocumentAttachment, refetchAttachmentsConfig);

  const [attachments, setAttachments] = useState(documentExtraAttachmentsData?.getDocumentById?.extraAttachments);

  const alreadyLinkedDocumentIds = useMemo(() => {
    if (!attachments) return [];
    return attachments
      .filter((a) => a.linkedDocument)
      .map((a) => a.linkedDocument!.id);
  }, [attachments]);

  const handleRemoveAttachmentClick = async (id: any) => {
    await removeDocumentExtraAttachment({ variables: { documentExtraAttachmentId: String(id) } });
  };

  const handleLinkDealDocuments = useCallback(async (selectedDocumentIds: string[]) => {
    if (!documentId || selectedDocumentIds.length === 0) return;
    try {
      await linkDealDocumentAttachment({
        variables: {
          targetDocumentId: String(documentId),
          sourceDocumentIds: selectedDocumentIds,
        },
      });
      setShowDealDocPicker(false);
    } catch (error) {
      console.error('Failed to link deal documents:', error);
    }
  }, [documentId, linkDealDocumentAttachment]);

  const handleMoveItem = async (dragIndex: number, hoverIndex: number) => {
    if (!attachments) {
      console.error('Attachments are not available.');
      return;
    }

    const updatedAttachments = [...attachments];
    const [movedItem] = updatedAttachments.splice(dragIndex, 1);
    updatedAttachments.splice(hoverIndex, 0, movedItem);

    const attachmentIds = updatedAttachments.map((item) => item.id);
    const idToIndexMap = new Map(attachmentIds.map((id, index) => [id, index]));

    updatedAttachments.sort((a, b) => {
      const indexA = idToIndexMap.get(a.id);
      const indexB = idToIndexMap.get(b.id);

      if (indexA === undefined || indexB === undefined) {
        return 0;
      }

      return indexA - indexB;
    });

    const orderedAttachmentIds = updatedAttachments.map((item) => String(item.id));

    await reorderDocumentExtraAttachments({
      variables: {
        documentId: String(documentId),
        orderedExtraAttachmentIds: orderedAttachmentIds,
      },
    });

    const reorderedAttachments = updatedAttachments.map((item, index) => ({ ...item, order: index }));
    setAttachments(reorderedAttachments);
  };

  useEffect(() => {
    if (documentExtraAttachmentsData) {
      setAttachments(documentExtraAttachmentsData.getDocumentById.extraAttachments);
    }
  }, [documentExtraAttachmentsData]);

  return (
    <>
      <DocumentAttachmentsSidebar
        handleFileUploadModal={handleFileUploadModal}
        handleDealDocumentPickerModal={handleDealDocumentPickerModal}
        handleRemoveAttachmentClick={handleRemoveAttachmentClick}
        attachments={attachments || []}
        moveItem={handleMoveItem}
        canUpload={canUploadExtraAttachments}
      />
      <FileUploadModal
        onClose={() => setActiveModal(null)}
        isOpen={activeModal === ModalActionsEnum.UPLOAD_ATTACHMENT_FILES}
        dealId={dealId}
        teamId={teamId}
      />
      <DealDocumentPickerModal
        show={showDealDocPicker}
        onClose={() => setShowDealDocPicker(false)}
        onConfirm={handleLinkDealDocuments}
        dealId={dealId}
        currentDocumentId={String(documentId)}
        alreadyLinkedDocumentIds={alreadyLinkedDocumentIds}
        loading={linkLoading}
      />
    </>
  );
};

export default DocumentAttachmentsSidebarContainer;
